import { memo, useCallback, useState, useRef, useEffect } from "react";
import { Input } from "@stockline/ui/components/input";
import { Loader2, Pencil } from "lucide-react";
import { useUpdatePreOrder } from "@/query/hooks/usePreOrders";
import { useFeatureFlag } from "@/query/hooks/useFeatureFlags";
import { UUID } from "crypto";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { CopyButton } from "@/components/ui/CopyButton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@stockline/ui/components/popover";

interface EditablePreOrderNumberProps {
  preOrderId: string;
  currentNumber?: string | null;
  customerPoNumber?: string | null;
  className?: string;
  readOnly?: boolean;
}

/** Activation source — drives lazy-wrapper hydration mode. */
type EpoNumberActivation = "click" | "focus" | "hover";

/**
 * Render-only trigger for the lazy wrapper below. Same DOM as the full
 * editor's PopoverTrigger child, but no Radix Popover root. Skipping the
 * Radix mount cuts ~12 ms of `getAnimationName` forced reflow per PO on
 * partner-card open — and since this control is mounted exactly once
 * per PO header (regardless of state), it's a flat win.
 */
function EditablePreOrderNumberShell({
  currentNumber,
  customerPoNumber,
  className = "",
  readOnly = false,
  onActivate,
}: EditablePreOrderNumberProps & {
  onActivate: (source: EpoNumberActivation) => void;
}) {
  const { t } = useTranslation();
  const hasInternal = !!currentNumber;
  const hasExternal = !!customerPoNumber;
  const hasCopyable = hasInternal || hasExternal;

  const copyText = [currentNumber, customerPoNumber]
    .filter(Boolean)
    .join(" / ");

  return (
    <div
      className={`group/order-number relative min-w-[140px] max-w-[260px] ${className}`}
      // Click MUST distinguish itself from hover/focus — the lazy wrapper
      // uses the activation source to decide whether to auto-open the
      // editor on first mount. Without it, a click on the cold shell
      // hydrates the inner component closed and the user has to click
      // again to actually open the popover.
      onClick={(e) => {
        e.stopPropagation();
        onActivate("click");
      }}
      onPointerEnter={() => onActivate("hover")}
      onFocus={() => onActivate("focus")}
    >
      <div className="px-2 py-0.5 rounded cursor-pointer border border-gray-200 hover:border-gray-400 hover:bg-gray-50 relative w-full flex items-center transition-all duration-200 ease-in-out text-sm">
        <div className="w-full min-w-0">
          {!hasInternal && !hasExternal ? (
            <div className="flex flex-col gap-0 min-w-0 overflow-hidden leading-tight">
              <span className="text-[9px] font-medium text-gray-400 uppercase truncate">
                {t("ordersRecap.internalNumberLabel")} /
              </span>
              <span className="text-[9px] font-medium text-gray-400 uppercase truncate">
                {t("ordersRecap.externalNumberLabel")}
              </span>
            </div>
          ) : (
            <div className="flex flex-col gap-0 min-w-0 overflow-hidden leading-tight">
              <div className="flex items-center gap-1 min-w-0">
                <span className="text-[9px] font-medium text-gray-400 uppercase shrink-0">
                  {t("ordersRecap.internalNumberLabel")}
                </span>
                <span
                  className="text-gray-700 truncate text-[11px]"
                  data-testid="preorder-number-internal"
                >
                  {hasInternal ? currentNumber : "—"}
                </span>
              </div>
              <div className="flex items-center gap-1 min-w-0">
                <span className="text-[9px] font-medium text-gray-400 uppercase shrink-0">
                  {t("ordersRecap.externalNumberLabel")}
                </span>
                <span
                  className="text-blue-600 truncate text-[11px]"
                  data-testid="preorder-number-external"
                >
                  {hasExternal ? customerPoNumber : "—"}
                </span>
              </div>
            </div>
          )}
        </div>
        {hasCopyable && (
          <CopyButton
            text={copyText}
            iconClassName="h-3 w-3"
            className="opacity-0 group-hover/order-number:opacity-100 transition-all duration-300 ease-in-out absolute right-5 top-1/2 -translate-y-1/2 flex-shrink-0"
          />
        )}
        {!readOnly && (
          <Pencil className="h-3 w-3 text-gray-400 opacity-0 group-hover/order-number:opacity-100 transition-all duration-300 ease-in-out absolute right-1 top-1/2 -translate-y-1/2 flex-shrink-0" />
        )}
      </div>
    </div>
  );
}

function EditablePreOrderNumberInner({
  preOrderId,
  currentNumber,
  customerPoNumber,
  className = "",
  readOnly = false,
  openOnMount = false,
}: EditablePreOrderNumberProps & { openOnMount?: boolean }) {
  const { t } = useTranslation();

  // Vivo: pre-order number is managed by M3, never user-editable
  // But customer_po_number is always editable (unless fully readOnly)
  const exportMode = useFeatureFlag("export_mode");
  const internalReadOnly = readOnly || exportMode === "vivo";

  // `openOnMount` arrives when the lazy wrapper hydrates from a click —
  // start open so a single click lands the user in the editor.
  const [isOpen, setIsOpen] = useState(openOnMount);
  const [internalValue, setInternalValue] = useState(currentNumber || "");
  const [externalValue, setExternalValue] = useState(customerPoNumber || "");
  const [prevInternal, setPrevInternal] = useState(currentNumber || "");
  const [prevExternal, setPrevExternal] = useState(customerPoNumber || "");
  const internalRef = useRef<HTMLInputElement>(null);
  const updateMutation = useUpdatePreOrder();

  useEffect(() => {
    setInternalValue(currentNumber || "");
    setPrevInternal(currentNumber || "");
  }, [currentNumber]);

  useEffect(() => {
    setExternalValue(customerPoNumber || "");
    setPrevExternal(customerPoNumber || "");
  }, [customerPoNumber]);

  useEffect(() => {
    if (isOpen && internalRef.current) {
      internalRef.current.focus();
    }
  }, [isOpen]);

  const handleSave = async () => {
    const internalChanged = !internalReadOnly && internalValue !== prevInternal;
    const externalChanged = !readOnly && externalValue !== prevExternal;

    if (!internalChanged && !externalChanged) {
      setIsOpen(false);
      return;
    }

    try {
      const data: Record<string, string | null> = {};
      if (internalChanged) data.pre_order_number = internalValue || null;
      if (externalChanged) data.customer_po_number = externalValue || null;

      await updateMutation.mutateAsync({
        id: preOrderId as UUID,
        data,
      });
      setPrevInternal(internalValue);
      setPrevExternal(externalValue);
      setIsOpen(false);
      toast.success(t("ordersRecap.preOrderNumberUpdated"));
    } catch {
      setInternalValue(prevInternal);
      setExternalValue(prevExternal);
      setIsOpen(false);
      toast.error(t("ordersRecap.preOrderNumberUpdateFailed"));
    }
  };

  const handleCancel = () => {
    setInternalValue(prevInternal);
    setExternalValue(prevExternal);
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };

  const isLoading = updateMutation.isPending;
  const hasInternal = !!internalValue;
  const hasExternal = !!externalValue;

  const renderDisplay = () => {
    if (!hasInternal && !hasExternal) {
      return (
        <div className="flex flex-col gap-0 min-w-0 overflow-hidden leading-tight">
          <span className="text-[9px] font-medium text-gray-400 uppercase truncate">
            {t("ordersRecap.internalNumberLabel")} /
          </span>
          <span className="text-[9px] font-medium text-gray-400 uppercase truncate">
            {t("ordersRecap.externalNumberLabel")}
          </span>
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-0 min-w-0 overflow-hidden leading-tight">
        <div className="flex items-center gap-1 min-w-0">
          <span className="text-[9px] font-medium text-gray-400 uppercase shrink-0">
            {t("ordersRecap.internalNumberLabel")}
          </span>
          <span
            className="text-gray-700 truncate text-[11px]"
            data-testid="preorder-number-internal"
          >
            {hasInternal ? internalValue : "—"}
          </span>
        </div>
        <div className="flex items-center gap-1 min-w-0">
          <span className="text-[9px] font-medium text-gray-400 uppercase shrink-0">
            {t("ordersRecap.externalNumberLabel")}
          </span>
          <span
            className="text-blue-600 truncate text-[11px]"
            data-testid="preorder-number-external"
          >
            {hasExternal ? externalValue : "—"}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`group/order-number relative min-w-[140px] max-w-[260px] ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      <Popover
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) handleSave();
          else setIsOpen(true);
        }}
      >
        <PopoverTrigger asChild>
          <div className="px-2 py-0.5 rounded cursor-pointer border border-gray-200 hover:border-gray-400 hover:bg-gray-50 relative w-full flex items-center transition-all duration-200 ease-in-out text-sm">
            <div className="w-full min-w-0">{renderDisplay()}</div>
            {(hasInternal || hasExternal) && (
              <CopyButton
                text={[internalValue, externalValue]
                  .filter(Boolean)
                  .join(" / ")}
                iconClassName="h-3 w-3"
                className="opacity-0 group-hover/order-number:opacity-100 transition-all duration-300 ease-in-out absolute right-5 top-1/2 -translate-y-1/2 flex-shrink-0"
              />
            )}
            {!readOnly && (
              <Pencil className="h-3 w-3 text-gray-400 opacity-0 group-hover/order-number:opacity-100 transition-all duration-300 ease-in-out absolute right-1 top-1/2 -translate-y-1/2 flex-shrink-0" />
            )}
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="w-56 p-3"
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="flex flex-col gap-2">
            <div>
              <label className="text-[10px] font-medium text-gray-500 uppercase">
                {t("ordersRecap.internalNumberLabel")}
              </label>
              <Input
                ref={internalReadOnly ? undefined : internalRef}
                type="text"
                value={internalValue}
                onChange={(e) => setInternalValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t("ordersRecap.preOrderNumberPlaceholder")}
                disabled={isLoading || internalReadOnly}
                className="h-7 text-xs"
              />
            </div>
            <div>
              <label className="text-[10px] font-medium text-gray-500 uppercase">
                {t("ordersRecap.externalNumberLabel")}
              </label>
              <Input
                ref={internalReadOnly && !readOnly ? internalRef : undefined}
                type="text"
                value={externalValue}
                onChange={(e) => setExternalValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t("ordersRecap.customerPoNumberPlaceholder")}
                disabled={isLoading || readOnly}
                className="h-7 text-xs"
              />
            </div>
          </div>
          {isLoading && (
            <div className="flex justify-center mt-2">
              <Loader2 className="h-3 w-3 animate-spin text-gray-400" />
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * Lazy wrapper: mounts the shell (no Radix) until first hover/focus/
 * click, then swaps to the full Radix-backed editor. Click hydration
 * sets `openOnMount` so a single click lands on the open popover (no
 * double-click race). Hover/focus hydration covers mouse + keyboard
 * users without auto-opening.
 */
function EditablePreOrderNumberLazy(props: EditablePreOrderNumberProps) {
  const [activation, setActivation] = useState<EpoNumberActivation | null>(
    null,
  );
  // Preserve the first activation source — once hydrated, the inner
  // component owns the popover and subsequent events are routed through
  // its own Radix handlers. The `prev ?? source` guard is what makes
  // first-click hydration land as `"click"` instead of `"hover"` /
  // `"focus"` from prior pointer events.
  const activate = useCallback((source: EpoNumberActivation) => {
    setActivation((prev) => prev ?? source);
  }, []);

  if (activation) {
    return (
      <EditablePreOrderNumberInner
        {...props}
        openOnMount={activation === "click"}
      />
    );
  }
  return <EditablePreOrderNumberShell {...props} onActivate={activate} />;
}

export const EditablePreOrderNumber = memo(EditablePreOrderNumberLazy);
